import { tr, usePartnerTranslation } from "../../../lib/language";
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { Check, Plus, X, Copy, ChevronDown, ChevronUp, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  FieldRow,
  SectionHeader,
  TextInput,
  TextArea,
  BulletTextArea,
  SelectField,
  SearchableMultiSelect,
  RadioCards,
  CheckboxField,
} from "./fields";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../../components/ui/dialog";
import { LocationTextInput } from "./LocationInputs";
import {
  CONTACT_INFO_ERROR,
  containsContactInfo,
} from "../../../lib/productTextSafety";
import {
  MEETING_POINT_TYPES,
  PRIVACY_TYPES,
  GUIDE_LANGUAGES,
  WEEKDAYS,
  CUTOFF_OPTIONS,
  INTERVAL_OPTIONS,
  OPTION_DURATION_UNITS,
  DURATION_OR_VALIDITY,
  VALIDITY_TYPES,
  PICKUP_TRANSPORT_MODES,
  AVAILABILITY_MODES,
  createBlankOption,
} from "./wizardConstants";
import { cn } from "../../../lib/utils";

import api from "../../../lib/api";

const CommissionContext = createContext(null);

const OPTION_NAME_MIN = 10;
const OPTION_NAME_MAX = 60;
const OPTION_REFERENCE_CODE_MAX = 20;
const CAPACITY_MAX_LIMIT = 999;
const PRICE_INTEGER_DIGITS = 7;
const PRICE_DECIMAL_DIGITS = 2;
const LANGUAGE_NOT_APPLICABLE = "Not Applicable";

const toLocalDateString = (date) => date.toLocaleDateString("en-CA");

const addYears = (base, years) => {
  const next = new Date(base);
  next.setFullYear(next.getFullYear() + years);
  return next;
};

const clampDateString = (value, min, max) => {
  const next = String(value || "").slice(0, 10);
  if (!next) return "";
  if (min && next < min) return min;
  if (max && next > max) return max;
  return next;
};

const sanitizePriceInput = (value) => {
  const raw = String(value || "").replace(/[^\d.]/g, "");
  const [integerRaw = "", ...decimalParts] = raw.split(".");
  const integer = integerRaw.slice(0, PRICE_INTEGER_DIGITS);
  const decimalRaw = decimalParts.join("");
  if (!decimalParts.length) return integer;
  return `${integer}.${decimalRaw.slice(0, PRICE_DECIMAL_DIGITS)}`;
};

const LooksGood = () => {
  usePartnerTranslation();
  return <span className="inline-flex items-center gap-1 font-semibold text-[#0F7A3B]">
    <Check className="h-3.5 w-3.5" strokeWidth={2.2} />{tr("Looks good")}</span>;
};

const contactErrorFor = (value) =>
  containsContactInfo(value) ? CONTACT_INFO_ERROR : "";

/* ---------- Pricing helpers ---------- */
/* Format a decimal price string with the supplier's configured currency. */
const fmtMoney = (n, currency = "USD") => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(v);
  } catch {
    return v.toFixed(2);
  }
};
const calcPayout = (price, commissionRate) => {
  const v = Number(price);
  if (!Number.isFinite(v) || v < 0) return null;
  return commissionRate == null ? null : v * (1 - commissionRate);
};

/* "From" of every tier row derives from previous row's "to + 1". The very
 * first row is always "1". */
const tierRangeFrom = (tiers, idx) => {
  if (idx === 0) return 1;
  const prev = tiers[idx - 1];
  const prevTo = Number(prev?.to);
  if (!Number.isFinite(prevTo) || prevTo < 1) return 1;
  return prevTo + 1;
};

/* ---------- Per-person tier table (used for both flat + per-category) ---------- */
const PerPersonTierTable = ({
  tiers,
  onChange,
  capacityMax,
  testid,
  currency = "USD",
}) => {
  usePartnerTranslation();
  const commissionRate = useContext(CommissionContext);
  const setTier = (i, key, value) =>
    onChange(tiers.map((t, idx) => (idx === i ? { ...t, [key]: value } : t)));
  const removeTier = (i) => {
    if (tiers.length === 1) {
      toast.error(tr("Keep at least one row"));
      return;
    }
    onChange(tiers.filter((_, idx) => idx !== i));
  };
  const addTier = () => onChange([...tiers, { to: "", price: "" }]);

  // Capacity max parsed once — feeds the auto-locked "to" on the last row.
  const capMaxNum =
    capacityMax !== "" && capacityMax !== null && capacityMax !== undefined
      ? Number(capacityMax)
      : null;

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="hidden grid-cols-[1.4fr,1fr,0.8fr,1fr,32px] gap-3 px-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-[#9BA1AB] sm:grid">
        <span>{tr("Number of people")}</span>
        <span>{tr("Customer pays")}</span>
        <span>{tr("Commission")}</span>
        <span>{tr("You will receive")}</span>
        <span aria-hidden />
      </div>
      {tiers.map((t, i) => {
        const fromQty = tierRangeFrom(tiers, i);
        const isLast = i === tiers.length - 1;
        // The last tier always ends at the capacity max — read-only when set.
        const lastToValue =
          isLast && Number.isFinite(capMaxNum) && capMaxNum > 0
            ? String(capMaxNum)
            : t.to;
        const payout = calcPayout(t.price, commissionRate);
        const lockLastTo =
          isLast && Number.isFinite(capMaxNum) && capMaxNum > 0;
        return (
          <div
            key={i}
            data-testid={`${testid}-row-${i}`}
            className="grid grid-cols-1 gap-2 rounded-lg border border-[#EAECEF] bg-white p-3 sm:grid-cols-[1.4fr,1fr,0.8fr,1fr,32px] sm:items-center sm:gap-3"
          >
            <div className="flex items-center gap-1.5 text-sm text-[#0F1115]">
              <span className="font-semibold">{fromQty}</span>
              <span className="text-[#9BA1AB]">{tr("to")}</span>
              <input
                type="number"
                min={fromQty}
                max={Number.isFinite(capMaxNum) ? capMaxNum : undefined}
                value={lastToValue}
                onChange={(e) => {
                  if (lockLastTo) return; // read-only
                  setTier(i, "to", e.target.value);
                }}
                readOnly={lockLastTo}
                placeholder={String(fromQty)}
                data-testid={`${testid}-to-${i}`}
                style={{ width: "5rem" }}
                className={cn(
                  "humrahe-input h-9 px-2 text-center",
                  lockLastTo && "cursor-not-allowed bg-[#F3F4F6] text-[#6B7280]",
                )}
              />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-[#6B7280]">{currency}</span>
              <input
                type="number"
                min={0}
                step="0.01"
                value={t.price}
                onChange={(e) => setTier(i, "price", sanitizePriceInput(e.target.value))}
                placeholder="0.00"
                data-testid={`${testid}-price-${i}`}
                inputMode="decimal"
                className="humrahe-input h-9 px-2"
              />
            </div>
            <span
              className="rounded-md bg-[#F3F4F6] px-2 py-1.5 text-center text-xs font-semibold text-[#6B7280]"
              data-testid={`${testid}-commission-${i}`}
            >
              {commissionRate == null ? "—" : `${Number((commissionRate * 100).toFixed(2))}%`}
            </span>
            <span
              className="text-sm font-bold text-[#0F1115]"
              data-testid={`${testid}-payout-${i}`}
            >
              {payout === null ? "—" : fmtMoney(payout, currency)}
            </span>
            <button
              type="button"
              onClick={() => removeTier(i)}
              disabled={tiers.length === 1 && isLast}
              aria-label={tr("Remove row")}
              data-testid={`${testid}-remove-${i}`}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[#6B7280] hover:bg-[#FFEFEF] hover:text-[#9B0000] disabled:cursor-not-allowed disabled:opacity-30"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
      <button
        type="button"
        onClick={addTier}
        data-testid={`${testid}-add`}
        className="inline-flex h-9 items-center gap-1.5 rounded-md border border-dashed border-[#0F1115] bg-white px-3 text-xs font-semibold text-[#0F1115] transition-colors hover:bg-[#F3EEEE]"
      >
        <Plus className="h-3.5 w-3.5" />{tr("Tier price")}</button>
    </div>
  );
};

/* ---------- Schedule date pickers (rendered inside the Schedule field) ---------- */
const ScheduleDates = ({ option, setOption, errors = {} }) => {
  usePartnerTranslation();
  // YYYY-MM-DD in the supplier's local timezone — used as the floor for
  // both date inputs so suppliers can't schedule into the past.
  const todayStr = new Date().toLocaleDateString("en-CA");
  const startMin = todayStr;
  const startMax = toLocalDateString(addYears(new Date(), 1));
  const endMax = toLocalDateString(addYears(new Date(), 10));
  // End date can't be before the chosen start date (or today, if no start).
  const endMin = option.schedule_start_date || todayStr;
  const hasEndDate = !!(option.has_schedule_end_date || option.schedule_end_date);
  return (
    <div
      data-testid="schedule-dates"
      className="space-y-3"
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div data-error-anchor={errors.schedule_start_date ? "true" : undefined}>
          <label className="text-xs font-semibold text-[#6B7280]">{tr("Activity start date")}</label>
          <input
            type="date"
            value={option.schedule_start_date || ""}
            min={startMin}
            max={startMax}
            onChange={(e) => {
              const v = clampDateString(e.target.value, startMin, startMax);
              // Bump the end date forward when the new start date is later
              // than the existing end date, so the end never sits in the past.
              const next = { ...option, schedule_start_date: v };
              if (
                option.schedule_end_date &&
                v &&
                option.schedule_end_date < v
              ) {
                next.schedule_end_date = v;
              }
              setOption(next);
            }}
            className="humrahe-input"
            data-testid="schedule-start-date"
          />
          {errors.schedule_start_date && (
            <p className="mt-1 text-xs font-medium text-[#9B0000]">
              {errors.schedule_start_date}
            </p>
          )}
        </div>
        {hasEndDate && (
          <div data-error-anchor={errors.schedule_end_date ? "true" : undefined}>
            <label className="text-xs font-semibold text-[#6B7280]">{tr("Activity end date")}</label>
            <input
              type="date"
              value={option.schedule_end_date || ""}
              min={endMin}
              max={endMax}
              onChange={(e) =>
                setOption({
                  ...option,
                  has_schedule_end_date: true,
                  schedule_end_date: clampDateString(e.target.value, endMin, endMax),
                })
              }
              className="humrahe-input"
              data-testid="schedule-end-date"
            />
            {errors.schedule_end_date && (
              <p className="mt-1 text-xs font-medium text-[#9B0000]">
                {errors.schedule_end_date}
              </p>
            )}
          </div>
        )}
      </div>
      <CheckboxField
        value={hasEndDate}
        onChange={(checked) =>
          setOption({
            ...option,
            has_schedule_end_date: checked,
            schedule_end_date: checked ? option.schedule_end_date || "" : "",
          })
        }
        label={tr("This activity has an end date")}
        testid="schedule-has-end-date"
      />
    </div>
  );
};

/* ---------- Schedule generator + slot list (rendered after the availability radio) ---------- */
const ScheduleGenerator = ({ option, setOption }) => {
  usePartnerTranslation();
  const isOpeningHours = option.availability_mode === "opening_hours";
  const [interval, setInterval] = useState(60);
  const [fromTime, setFromTime] = useState("09:00");
  const [toTime, setToTime] = useState("17:00");
  const [days, setDays] = useState({});

  const toggleDay = (key) =>
    setDays((d) => ({ ...d, [key]: !d[key] }));

  const generate = () => {
    const selectedDays = WEEKDAYS.filter((w) => days[w.key]).map((w) => w.key);
    if (selectedDays.length === 0) {
      toast.error(tr("Pick at least one day"));
      return;
    }
    if (!fromTime || !toTime) {
      toast.error(
        isOpeningHours ? tr("Set opening and closing time") : tr("Set start and end times"),
      );
      return;
    }
    const [fh, fm] = fromTime.split(":").map(Number);
    const [th, tm] = toTime.split(":").map(Number);
    const startMin = fh * 60 + fm;
    const endMin = th * 60 + tm;
    if (endMin <= startMin) {
      toast.error(
        isOpeningHours
          ? tr("Closing time must be after opening time")
          : tr("End time must be after start time"),
      );
      return;
    }

    // Helper: cosmetic day label (Mon, Tue, …) for error messages.
    const dayLabel = (key) =>
      (WEEKDAYS.find((w) => w.key === key) || {}).label || key;

    const existing = option.schedule_slots || [];

    if (isOpeningHours) {
      // Detect overlaps: a new window [startMin, endMin) conflicts with any
      // existing window on the same day where the two intervals overlap.
      // Touching at the boundary (e.g. 09:00–12:00 then 12:00–15:00) is fine.
      const conflicts = [];
      selectedDays.forEach((day) => {
        existing
          .filter((s) => s.day === day && s.end_time)
          .forEach((s) => {
            const [eh1, em1] = s.time.split(":").map(Number);
            const [eh2, em2] = s.end_time.split(":").map(Number);
            const exStart = eh1 * 60 + em1;
            const exEnd = eh2 * 60 + em2;
            if (startMin < exEnd && endMin > exStart) {
              conflicts.push({
                day,
                window: `${s.time} – ${s.end_time}`,
              });
            }
          });
      });
      if (conflicts.length > 0) {
        const list = conflicts
          .slice(0, 3)
          .map((c) => `${dayLabel(c.day)} (${c.window})`)
          .join(", ");
        const more = conflicts.length > 3 ? ` and ${conflicts.length - 3} more` : "";
        toast.error(tr("Those hours overlap with existing opening hours"), {
          description: tr("Conflicts with {{value0}}{{value1}}. Adjust the open / close window or clear the conflicting day first.", { value0: list, value1: more }),
        });
        return;
      }

      const newWindows = selectedDays.map((day) => ({
        day,
        time: fromTime,
        end_time: toTime,
      }));
      setOption({ ...option, schedule_slots: [...existing, ...newWindows] });
      toast.success(
        tr("Opening hours added; days: {{count}}", { count: newWindows.length }),
      );
      return;
    }

    if (interval < 5 || interval > 240) {
      toast.error(tr("Interval must be 5–240 minutes"));
      return;
    }

    // Time-slots mode: build the candidate list of (day, time) pairs and
    // detect conflicts against existing point-in-time slots on the same day.
    const candidates = [];
    for (let m = startMin; m <= endMin; m += interval) {
      const h = Math.floor(m / 60).toString().padStart(2, "0");
      const mm = (m % 60).toString().padStart(2, "0");
      const time = `${h}:${mm}`;
      selectedDays.forEach((day) => candidates.push({ day, time }));
    }
    const existingKeys = new Set(existing.map((s) => `${s.day}-${s.time}`));
    const conflicts = candidates.filter((c) =>
      existingKeys.has(`${c.day}-${c.time}`),
    );
    if (conflicts.length > 0) {
      // Group conflicts by day for a more readable message.
      const byDay = {};
      conflicts.forEach((c) => {
        byDay[c.day] = byDay[c.day] || [];
        byDay[c.day].push(c.time);
      });
      const lines = Object.keys(byDay).map((day) => {
        const times = byDay[day];
        const preview = times.slice(0, 3).join(", ");
        const extra = times.length > 3 ? ` +${times.length - 3} more` : "";
        return `${dayLabel(day)}: ${preview}${extra}`;
      });
      toast.error(
        tr("Already scheduled times: {{count}}", { count: conflicts.length }),
        { description: tr("Already added — {{value0}}.", { value0: lines.join(" · ") }) },
      );
      return;
    }

    setOption({ ...option, schedule_slots: [...existing, ...candidates] });
    toast.success(
      tr("Times added: {{count}}", { count: candidates.length }),
    );
  };

  const removeSlot = (idx) => {
    setOption({
      ...option,
      schedule_slots: option.schedule_slots.filter((_, i) => i !== idx),
    });
  };

  const clearSlotsForDay = (dayKey) => {
    setOption({
      ...option,
      schedule_slots: option.schedule_slots.filter((s) => s.day !== dayKey),
    });
  };

  // Group slots by day
  const grouped = WEEKDAYS.map((w) => ({
    ...w,
    entries: (option.schedule_slots || [])
      .map((s, i) => ({ ...s, idx: i }))
      .filter((s) => s.day === w.key),
  }));

  const generatorTitle = isOpeningHours ? tr("Set opening hours") : tr("Generate time slots");
  const generatorSubtitle = isOpeningHours
    ? tr("Pick the days the option runs and the daily open / close window.")
    : tr("Create a start time at regular intervals between an opening and closing time, then apply to one or more days.");
  const fromLabel = isOpeningHours ? tr("Opens at") : "Between (start)";
  const toLabel = isOpeningHours ? tr("Closes at") : "And (end)";
  const applyLabel = tr("Apply to selected days");
  const slotsTitle = isOpeningHours ? tr("Weekly opening hours") : tr("All time slots");
  const slotsSubtitle = isOpeningHours
    ? tr("Tap a day's window to remove it.")
    : tr("Tap a slot to remove it.");

  if (!option.availability_mode) return null;

  return (
    <div className="space-y-5" data-testid="schedule-generator">
      <div className="rounded-lg border border-[#F0F2F5] bg-[#FAFAFB] p-4">
        <p className="text-sm font-semibold text-[#0F1115]">{generatorTitle}</p>
        <p className="mt-0.5 text-xs text-[#6B7280]">{generatorSubtitle}</p>

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
          {!isOpeningHours && (
            <div>
              <label className="text-xs font-semibold text-[#6B7280]">{tr("Every")}</label>
              <SelectField
                value={interval}
                onChange={(v) => setInterval(Number(v))}
                options={INTERVAL_OPTIONS.map((m) => ({
                  value: m,
                  label: `${m} minutes`,
                }))}
                testid="schedule-interval"
              />
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-[#6B7280]">
              {fromLabel}
            </label>
            <input
              type="time"
              step={300}
              value={fromTime}
              onChange={(e) => setFromTime(e.target.value)}
              className="humrahe-input"
              data-testid="schedule-from-time"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#6B7280]">
              {toLabel}
            </label>
            <input
              type="time"
              step={300}
              value={toTime}
              onChange={(e) => setToTime(e.target.value)}
              className="humrahe-input"
              data-testid="schedule-to-time"
            />
          </div>
        </div>

        <div className="mt-4">
          <p className="text-xs font-semibold text-[#6B7280]">{tr("Apply to days")}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {WEEKDAYS.map((w) => {
              const on = !!days[w.key];
              return (
                <button
                  type="button"
                  key={w.key}
                  data-testid={`schedule-day-${w.key}`}
                  onClick={() => toggleDay(w.key)}
                  aria-pressed={on}
                  className={cn(
                    "h-9 min-w-[3.25rem] rounded-md border px-2 text-xs font-semibold transition-colors",
                    on
                      ? "border-[#FF5757] bg-[#FFEFEF] text-[#9B0000]"
                      : "border-[#E6E8EC] bg-white text-[#0F1115] hover:border-[#0F1115]",
                  )}
                >
                  {w.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={generate}
            data-testid="schedule-apply"
            className="humrahe-btn-primary inline-flex h-10 items-center gap-1.5 rounded-md px-4 text-sm font-semibold"
          >
            {applyLabel}
          </button>
        </div>
      </div>

      <div data-testid="schedule-slots">
        <p className="text-sm font-semibold text-[#0F1115]">{slotsTitle}</p>
        <p className="mt-0.5 text-xs text-[#6B7280]">{slotsSubtitle}</p>
        <div className="mt-3 space-y-2">
          {grouped.map((g) => (
            <div
              key={g.key}
              data-testid={`schedule-day-row-${g.key}`}
              className="rounded-md border border-[#EAECEF] bg-white px-3 py-2"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-[#0F1115]">
                  {g.label}
                </span>
                {g.entries.length > 0 && (
                  <button
                    type="button"
                    onClick={() => clearSlotsForDay(g.key)}
                    data-testid={`schedule-clear-${g.key}`}
                    className="text-xs font-semibold text-[#9B0000] hover:underline"
                  >{tr("Clear")}</button>
                )}
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {g.entries.length === 0 ? (
                  <span className="text-xs italic text-[#9BA1AB]">
                    {isOpeningHours ? tr("Closed") : tr("No times yet")}
                  </span>
                ) : (
                  g.entries.map((s) => {
                    const label = s.end_time ? `${s.time} – ${s.end_time}` : s.time;
                    return (
                      <button
                        type="button"
                        key={`${g.key}-${s.idx}-${s.time}`}
                        onClick={() => removeSlot(s.idx)}
                        data-testid={`schedule-slot-${g.key}-${s.time}`}
                        className="inline-flex h-7 items-center gap-1 rounded-full bg-[#F3EEEE] px-2.5 text-xs font-medium text-[#0F1115] transition-colors hover:bg-[#FFEFEF] hover:text-[#9B0000]"
                      >
                        {label}
                        <X className="h-3 w-3" />
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ---------- Per-category pricing block ---------- */
/* Categories ordered top → bottom: optional Infant → Child → Adult.
 * Age ranges never overlap: each category's "from" derives from the
 * previous category's "to + 1". The last category (Adult) is always
 * open-ended on the upper bound. */
const CATEGORY_DEFS = {
  infant: { get label() { return tr("Infant"); }, default_age_max: 2 },
  child: { get label() { return tr("Child"); }, default_age_max: 12 },
  adult: { get label() { return tr("Adult"); }, default_age_max: null },
};
const ageFromFor = (cats, idx) => {
  if (idx === 0) return 0;
  const prev = cats[idx - 1];
  const prevMax = Number(prev?.age_max);
  if (!Number.isFinite(prevMax)) return 0;
  return prevMax + 1;
};

const PerCategoryPricing = ({ option, setOption, capacityMax, currency }) => {
  usePartnerTranslation();
  const cats = option.categories || [];
  const setCat = (i, patch) =>
    setOption({
      ...option,
      categories: cats.map((c, idx) => (idx === i ? { ...c, ...patch } : c)),
    });
  const hasInfant = cats.some((c) => c.key === "infant");
  const addInfant = () => {
    if (hasInfant) return;
    // Slot Infant in front of Child. Reset Child's age "from" implicitly
    // — we only have to set Infant's own age_max sensibly.
    setOption({
      ...option,
      categories: [
        {
          key: "infant",
          enabled: true,
          age_max: CATEGORY_DEFS.infant.default_age_max,
          tiers: [{ to: "", price: "" }],
        },
        ...cats,
      ],
    });
  };
  const removeInfant = () => {
    setOption({ ...option, categories: cats.filter((c) => c.key !== "infant") });
  };

  return (
    <div className="space-y-3" data-testid="per-category-pricing">
      {cats.map((c, i) => {
        const def = CATEGORY_DEFS[c.key] || { label: c.key, default_age_max: null };
        const ageFrom = ageFromFor(cats, i);
        const isLast = i === cats.length - 1;
        return (
          <div
            key={c.key}
            data-testid={`category-${c.key}`}
            className="rounded-lg border border-[#EAECEF] bg-[#FAFAFB] p-3 sm:p-4"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <span className="text-sm font-extrabold tracking-tight text-[#0F1115]">
                  {def.label}
                </span>
                <div className="flex items-center gap-1.5 text-xs text-[#6B7280]">
                  <span className="font-semibold text-[#0F1115]">{ageFrom}</span>
                  <span>{tr("to")}</span>
                  {isLast ? (
                    <span className="font-semibold text-[#0F1115]">+</span>
                  ) : (
                    <input
                      type="number"
                      min={ageFrom}
                      value={c.age_max ?? ""}
                      onChange={(e) =>
                        setCat(i, { age_max: e.target.value === "" ? "" : Number(e.target.value) })
                      }
                      data-testid={`category-${c.key}-age-max`}
                      style={{ width: "5rem" }}
                      className="humrahe-input h-8 px-2 text-center"
                    />
                  )}
                  <span>{tr("years")}</span>
                </div>
              </div>
              {c.key === "infant" && (
                <button
                  type="button"
                  onClick={removeInfant}
                  data-testid="category-infant-remove"
                  className="inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-xs font-semibold text-[#9B0000] hover:bg-[#FFEFEF]"
                >
                  <X className="h-3.5 w-3.5" />{tr("Remove")}</button>
              )}
            </div>
            <div className="mt-3">
              <PerPersonTierTable
                tiers={c.tiers || [{ to: "", price: "" }]}
                onChange={(tiers) => setCat(i, { tiers })}
                capacityMax={capacityMax}
                testid={`category-${c.key}-tiers`}
                currency={currency}
              />
            </div>
          </div>
        );
      })}
      {!hasInfant && (
        <button
          type="button"
          onClick={addInfant}
          data-testid="category-add-infant"
          className="inline-flex h-9 items-center gap-1.5 rounded-md border border-dashed border-[#CFD2D8] bg-white px-3 text-xs font-semibold text-[#0F1115] transition-colors hover:border-[#0F1115]"
        >
          <Plus className="h-3.5 w-3.5" />{tr("Add Infant category")}</button>
      )}
    </div>
  );
};

/* ---------- Group sizes editor (1 to X, X+1 to Y, …) ---------- */
const GroupSizesEditor = ({ option, setOption }) => {
  usePartnerTranslation();
  const sizes = option.group_sizes || [{ to: "" }];
  const setRow = (i, value) =>
    setOption({
      ...option,
      group_sizes: sizes.map((s, idx) => (idx === i ? { ...s, to: value } : s)),
    });
  const addRow = () =>
    setOption({ ...option, group_sizes: [...sizes, { to: "" }] });
  const removeRow = (i) => {
    if (sizes.length === 1) {
      toast.error(tr("Keep at least one group size"));
      return;
    }
    const nextSizes = sizes.filter((_, idx) => idx !== i);
    // Drop any orphan price entries whose upper bound no longer exists.
    const validTos = new Set(
      nextSizes
        .map((s) => Number(s.to))
        .filter((n) => Number.isFinite(n) && n > 0)
        .map(String),
    );
    const nextPrices = {};
    Object.entries(option.group_prices || {}).forEach(([k, v]) => {
      if (validTos.has(k)) nextPrices[k] = v;
    });
    setOption({ ...option, group_sizes: nextSizes, group_prices: nextPrices });
  };

  return (
    <div className="space-y-2" data-testid="group-sizes-editor">
      {sizes.map((s, i) => {
        const fromQty = i === 0 ? 1 : (Number(sizes[i - 1]?.to) || 0) + 1;
        return (
          <div
            key={i}
            data-testid={`group-size-row-${i}`}
            className="flex items-center gap-2 rounded-lg border border-[#EAECEF] bg-white p-3 text-sm"
          >
            <span className="font-semibold text-[#0F1115]">{fromQty}</span>
            <span className="text-[#9BA1AB]">{tr("to")}</span>
            <input
              type="number"
              min={fromQty}
              value={s.to}
              onChange={(e) => setRow(i, e.target.value)}
              placeholder={String(fromQty)}
              data-testid={`group-size-to-${i}`}
              style={{ width: "5rem" }}
              className="humrahe-input h-9 px-2 text-center"
            />
            <span className="text-xs text-[#6B7280]">{tr("people")}</span>
            <span className="ms-auto" />
            <button
              type="button"
              onClick={() => removeRow(i)}
              disabled={sizes.length === 1}
              aria-label={tr("Remove group size")}
              data-testid={`group-size-remove-${i}`}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[#6B7280] hover:bg-[#FFEFEF] hover:text-[#9B0000] disabled:cursor-not-allowed disabled:opacity-30"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
      <button
        type="button"
        onClick={addRow}
        data-testid="group-size-add"
        className="inline-flex h-9 items-center gap-1.5 rounded-md border border-dashed border-[#0F1115] bg-white px-3 text-xs font-semibold text-[#0F1115] transition-colors hover:bg-[#F3EEEE]"
      >
        <Plus className="h-3.5 w-3.5" />{tr("Add additional group size")}</button>
    </div>
  );
};

/* ---------- Per-group price table (auto-derived rows from group_sizes) ---------- */
const GroupPriceTable = ({ option, setOption, currency = "USD" }) => {
  usePartnerTranslation();
  const commissionRate = useContext(CommissionContext);
  const sizes = option.group_sizes || [];
  const prices = option.group_prices || {};
  const validRows = sizes
    .map((s) => Number(s.to))
    .filter((n) => Number.isFinite(n) && n > 0);
  const setPrice = (groupOf, value) => {
    setOption({
      ...option,
      group_prices: { ...prices, [String(groupOf)]: value },
    });
  };

  if (validRows.length === 0) {
    return (
      <p
        data-testid="group-price-empty"
        className="rounded-md border border-dashed border-[#CFD2D8] bg-white px-4 py-3 text-xs italic text-[#9BA1AB]"
      >{tr("Add group sizes above to set their prices.")}</p>
    );
  }

  return (
    <div className="space-y-2" data-testid="group-price-table">
      <div className="hidden grid-cols-[1.4fr,1fr,0.8fr,1fr] gap-3 px-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-[#9BA1AB] sm:grid">
        <span>{tr("Group size")}</span>
        <span>{tr("Group pays")}</span>
        <span>{tr("Commission")}</span>
        <span>{tr("Payout per group")}</span>
      </div>
      {validRows.map((upper) => {
        const v = prices[String(upper)] ?? "";
        const payout = calcPayout(v, commissionRate);
        return (
          <div
            key={upper}
            data-testid={`group-price-row-${upper}`}
            className="grid grid-cols-1 gap-2 rounded-lg border border-[#EAECEF] bg-white p-3 sm:grid-cols-[1.4fr,1fr,0.8fr,1fr] sm:items-center sm:gap-3"
          >
            <span className="text-sm font-bold text-[#0F1115]">{tr("Group of ")}{upper}
            </span>
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-[#6B7280]">
                {currency}
              </span>
              <input
                type="number"
                min={0}
                step="0.01"
                value={v}
                onChange={(e) => setPrice(upper, sanitizePriceInput(e.target.value))}
                placeholder="0.00"
                data-testid={`group-price-input-${upper}`}
                inputMode="decimal"
                className="humrahe-input h-9 px-2"
              />
            </div>
            <span className="rounded-md bg-[#F3F4F6] px-2 py-1.5 text-center text-xs font-semibold text-[#6B7280]">
              {commissionRate == null ? "—" : `${Number((commissionRate * 100).toFixed(2))}%`}
            </span>
            <span className="text-sm font-bold text-[#0F1115]">
              {payout === null ? "—" : fmtMoney(payout, currency)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

/* ---------- Single option editor ---------- */
/* Visible section grouping inside an option's form. Each section is a
 * mobile-first responsive card with a clear title row and the fields
 * underneath. Pure presentation — no behaviour change. */
const OptionSection = ({
  index,
  title,
  hint,
  children,
  testid
}) => {
  usePartnerTranslation();
  return <section data-testid={testid} className="rounded-xl border border-[#D9DDE5] bg-white p-4 shadow-[0_1px_0_rgba(15,17,21,0.03)] sm:p-5">
    <header className="flex items-baseline gap-2 border-b border-[#F0F2F5] pb-2.5">
      <span aria-hidden className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#0F1115] text-[10px] font-bold text-white">
        {index}
      </span>
      <h3 className="text-sm font-extrabold tracking-tight text-[#0F1115] sm:text-base">
        {title}
      </h3>
      {hint && <span className="hidden text-xs text-[#9BA1AB] sm:inline">— {hint}</span>}
    </header>
    <div className="mt-4 space-y-5">{children}</div>
  </section>;
};

const OptionEditor = ({ option, onChange, errors = {}, prefix = "option", currency }) => {
  usePartnerTranslation();
  const set = (key, value) => onChange({ ...option, [key]: value });
  const optionNameLength = (option.name || "").length;
  const optionNameContactError = contactErrorFor(option.name);
  const optionNameOk =
    optionNameLength >= OPTION_NAME_MIN && optionNameLength <= OPTION_NAME_MAX;
  const optionNameHelper =
    optionNameContactError
      ? ""
      : optionNameLength === 0
      ? `${OPTION_NAME_MIN}–${OPTION_NAME_MAX} characters.`
      : optionNameLength < OPTION_NAME_MIN
        ? `${OPTION_NAME_MIN - optionNameLength} more characters needed`
        : optionNameOk
          ? <LooksGood />
          : tr("Too long — please trim");
  const referenceLength = (option.reference_code || "").length;
  const optionNameError = errors.name || optionNameContactError;
  const referenceCodeError = errors.reference_code || contactErrorFor(option.reference_code);
  const languagesOtherError = errors.languages_other || contactErrorFor(option.languages_other);
  const meetingPointLocationError =
    errors.meeting_point_location || contactErrorFor(option.meeting_point_location);
  const meetingPointDescriptionError =
    errors.meeting_point_description || contactErrorFor(option.meeting_point_description);
  const pickupAreaError =
    errors.pickup_area_description || contactErrorFor(option.pickup_area_description);

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* ---------- 1. Option setup ---------- */}
      <OptionSection
        index={1}
        title={tr("Option setup")}
        hint="What this variant is and who it's for"
        testid={`${prefix}-section-setup`}
      >
        <div className="grid grid-cols-1 gap-5 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <FieldRow
            label={tr("Option name")}
            required
            testid={`${prefix}-field-name`}
            counter={`${optionNameLength} / ${OPTION_NAME_MAX}`}
            helper={optionNameHelper}
            error={optionNameError}
            info={tr("Customers see this when they have to pick between multiple options. Examples: 'Standard 3-hour walking tour', 'Private VIP after-hours visit'.")}
          >
            <TextInput
              value={option.name}
              onChange={(v) => set("name", v.slice(0, OPTION_NAME_MAX))}
              placeholder={tr("Standard 3-hour walking tour")}
              testid={`${prefix}-input-name`}
              maxLength={OPTION_NAME_MAX}
              error={optionNameError}
              enforceNoContactInfo
            />
          </FieldRow>
          <FieldRow
            label={tr("Option reference code")}
            testid={`${prefix}-field-reference-code`}
            counter={`${referenceLength} / ${OPTION_REFERENCE_CODE_MAX}`}
            helper={tr("Optional. Up to {{value0}} characters.", { value0: OPTION_REFERENCE_CODE_MAX })}
            error={referenceCodeError}
            info={tr("Internal code for your bookkeeping — customers don't see this.")}
          >
            <TextInput
              value={option.reference_code}
              onChange={(v) => set("reference_code", v.slice(0, OPTION_REFERENCE_CODE_MAX))}
              placeholder="STD-3H"
              testid={`${prefix}-input-reference-code`}
              maxLength={OPTION_REFERENCE_CODE_MAX}
              error={referenceCodeError}
              enforceNoContactInfo
            />
          </FieldRow>
        </div>

        <FieldRow
          label={tr("Languages this option is offered in")}
          required
          testid={`${prefix}-field-languages`}
          error={errors.languages || languagesOtherError}
          info={tr("Only pick languages you can actually deliver the tour in. Customers booking in a missing language are a top cause of refunds.")}
        >
          <div className="space-y-2">
            <SearchableMultiSelect
              values={option.languages}
              onChange={(v) => {
                const selectedNotApplicable = v.includes(LANGUAGE_NOT_APPLICABLE);
                const hadNotApplicable = (option.languages || []).includes(LANGUAGE_NOT_APPLICABLE);
                if (selectedNotApplicable && !hadNotApplicable) {
                  set("languages", [LANGUAGE_NOT_APPLICABLE]);
                  return;
                }
                set("languages", selectedNotApplicable ? v.filter((item) => item === LANGUAGE_NOT_APPLICABLE) : v);
              }}
              options={[...GUIDE_LANGUAGES, "Other"]}
              placeholder={tr("Select languages")}
              searchPlaceholder={tr("Search languages...")}
              testid={`${prefix}-chips-languages`}
              error={errors.languages}
              disabledValues={
                (option.languages || []).includes(LANGUAGE_NOT_APPLICABLE)
                  ? [...GUIDE_LANGUAGES, "Other"].filter((item) => item !== LANGUAGE_NOT_APPLICABLE)
                  : []
              }
            />
            {(option.languages || []).includes("Other") && (
              <TextInput
                value={option.languages_other}
                onChange={(v) => set("languages_other", v)}
                placeholder={tr("Add custom languages, separated by commas")}
                testid={`${prefix}-input-languages-other`}
                error={languagesOtherError}
                enforceNoContactInfo
              />
            )}
          </div>
        </FieldRow>

        <FieldRow
          label={tr("Private or group activity")}
          required
          testid={`${prefix}-field-privacy-type`}
          error={errors.privacy_type}
        >
          <RadioCards
            value={option.privacy_type}
            onChange={(v) => set("privacy_type", v)}
            options={PRIVACY_TYPES}
            testid={`${prefix}-radio-privacy-type`}
          />
        </FieldRow>

        <FieldRow
          label={tr("Duration or Validity: Which best describes your activity?")}
          required
          testid={`${prefix}-field-duration-or-validity`}
          error={errors.duration_or_validity}
        >
          <RadioCards
            value={option.duration_or_validity}
            onChange={(v) =>
              // Single onChange call so the second set doesn't shadow the
              // first when both updates collapse to one render. Switching to
              // "duration" wipes any stale validity_type from a previous pick.
              onChange({
                ...option,
                duration_or_validity: v,
                ...(v === "duration" ? { validity_type: "" } : {}),
              })
            }
            options={DURATION_OR_VALIDITY}
            testid={`${prefix}-radio-duration-or-validity`}
          />
        </FieldRow>

        {option.duration_or_validity === "duration" && (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <FieldRow
              label={tr("Duration")}
              required
              testid={`${prefix}-field-duration-value`}
              error={errors.duration_value}
            >
              <TextInput
                type="number"
                min={1}
                value={option.duration_value}
                onChange={(v) => set("duration_value", v)}
                placeholder="3"
                testid={`${prefix}-input-duration-value`}
                error={errors.duration_value}
              />
            </FieldRow>
            <FieldRow
              label={tr("Unit")}
              required
              testid={`${prefix}-field-duration-unit`}
              error={errors.duration_unit}
            >
              <SelectField
                value={option.duration_unit}
                onChange={(v) => set("duration_unit", v)}
                options={OPTION_DURATION_UNITS}
                testid={`${prefix}-select-duration-unit`}
              />
            </FieldRow>
          </div>
        )}

        {option.duration_or_validity === "validity" && (
          <div className="space-y-5">
            <FieldRow
              label={tr("Validity period")}
              required
              testid={`${prefix}-field-validity-type`}
              error={errors.validity_type}
            >
              <SelectField
                value={option.validity_type}
                onChange={(v) => set("validity_type", v)}
                options={VALIDITY_TYPES}
                placeholder={tr("Pick how the ticket stays valid")}
                testid={`${prefix}-select-validity-type`}
                error={errors.validity_type}
              />
            </FieldRow>

            {(option.validity_type === "from_booking" ||
              option.validity_type === "from_activation") && (
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <FieldRow
                  label={tr("Valid for")}
                  required
                  testid={`${prefix}-field-validity-value`}
                  error={errors.duration_value}
                >
                  <TextInput
                    type="number"
                    min={1}
                    value={option.duration_value}
                    onChange={(v) => set("duration_value", v)}
                    placeholder="3"
                    testid={`${prefix}-input-validity-value`}
                    error={errors.duration_value}
                  />
                </FieldRow>
                <FieldRow
                 label={tr("Unit")}
                 required
                 testid={`${prefix}-field-validity-unit`}
                  error={errors.duration_unit}
                >
                  <SelectField
                    value={option.duration_unit}
                    onChange={(v) => set("duration_unit", v)}
                    options={OPTION_DURATION_UNITS}
                    testid={`${prefix}-select-validity-unit`}
                  />
                </FieldRow>
              </div>
            )}
          </div>
        )}
      </OptionSection>

      {/* ---------- 2. Meeting point / pickup ---------- */}
      <OptionSection
        index={2}
        title={tr("Meeting point / pickup")}
        hint="Where the experience starts"
        testid={`${prefix}-section-meeting`}
      >
        <FieldRow
          label={tr("How do customers get to the activity?")}
          required
          testid={`${prefix}-field-meeting-point-type`}
          error={errors.meeting_point_type}
          info={tr("How will you meet with the customer or how will they reach to the activity venue?")}
        >
          <RadioCards
            value={option.meeting_point_type}
            onChange={(v) => set("meeting_point_type", v)}
            options={MEETING_POINT_TYPES}
            testid={`${prefix}-radio-meeting-point-type`}
          />
        </FieldRow>

        {option.meeting_point_type === "set_meeting_point" && (
          <FieldRow
            label={tr("Meeting point address or landmark")}
            required
            testid={`${prefix}-field-meeting-point-location`}
            error={meetingPointLocationError}
          >
            <div className="space-y-3">
              <LocationTextInput
                value={option.meeting_point_location}
                onChange={(v) => set("meeting_point_location", v)}
                onPick={(item) =>
                  onChange({
                    ...option,
                    meeting_point_location: item.full,
                    meeting_point_coords: item.coords || null,
                  })
                }
                placeholder={tr("Outside the Arch of Constantine, Piazza del Colosseo, Rome")}
                testid={`${prefix}-input-meeting-point-location`}
                error={meetingPointLocationError}
                enforceNoContactInfo
              />
            </div>
          </FieldRow>
        )}

        {option.meeting_point_type === "set_meeting_point" && (
          <FieldRow
            label={tr("Meeting point description")}
            testid={`${prefix}-field-meeting-point-description`}
            helper={tr("Mention anything that helps customers recognize the meeting point.")}
            error={meetingPointDescriptionError}
          >
            <TextArea
              value={option.meeting_point_description}
              onChange={(v) => set("meeting_point_description", v)}
              placeholder={tr("Look for the guide holding a red 'Voocly' sign next to the ticket booth.")}
              testid={`${prefix}-input-meeting-point-description`}
              error={meetingPointDescriptionError}
              enforceNoContactInfo
            />
          </FieldRow>
        )}

        {option.meeting_point_type === "pickup" && (
          <>
            <FieldRow
              label={tr("Pickup area or hotels")}
              required
              testid={`${prefix}-field-pickup-area`}
              error={pickupAreaError}
              helper={tr("Where should customers wait for their pickup and what should they look for while waiting?")}
            >
              <BulletTextArea
                value={option.pickup_area_description}
                onChange={(v) => set("pickup_area_description", v)}
                placeholder={tr("Pickup from any hotel within 5 km of Termini Station\nExact pickup time is emailed after booking")}
                rows={4}
                maxLength={400}
                testid={`${prefix}-input-pickup-area`}
                error={pickupAreaError}
                enforceNoContactInfo
              />
            </FieldRow>

            <FieldRow
              label={tr("How will customers be picked up?")}
              required
              testid={`${prefix}-field-pickup-transport-mode`}
              error={errors.pickup_transport_mode}
            >
              <SelectField
                value={option.pickup_transport_mode}
                onChange={(v) => set("pickup_transport_mode", v)}
                options={PICKUP_TRANSPORT_MODES}
                placeholder={tr("Pick a transport mode")}
                testid={`${prefix}-select-pickup-transport-mode`}
                error={errors.pickup_transport_mode}
              />
            </FieldRow>
          </>
        )}
      </OptionSection>

      {/* ---------- 3. Schedule & availability ---------- */}
      <OptionSection
        index={3}
        title={tr("Schedule & availability")}
        hint="When the experience runs"
        testid={`${prefix}-section-schedule`}
      >
        <FieldRow
          label={tr("Schedule")}
          required
          testid={`${prefix}-field-schedule`}
          error={errors.schedule}
          info={tr("Pick the activity dates here, then choose below how you want to set the daily availability.")}
        >
          <ScheduleDates option={option} setOption={onChange} errors={errors} />
        </FieldRow>

        <FieldRow
          label={tr("How do you set your availability?")}
          required
          testid={`${prefix}-field-availability-mode`}
          error={errors.availability_mode}
        >
          <RadioCards
            value={option.availability_mode}
            onChange={(v) =>
              // Wipe existing schedule slots when the mode changes so a tour
              // doesn't carry "10:00 AM" point-in-time slots into an
              // opening-hours layout (or vice versa).
              onChange({
                ...option,
                availability_mode: v,
                schedule_slots: [],
              })
            }
            options={AVAILABILITY_MODES}
            testid={`${prefix}-radio-availability-mode`}
          />
        </FieldRow>

        <ScheduleGenerator option={option} setOption={onChange} />
      </OptionSection>

      {/* ---------- 4. Pricing ---------- */}
      <OptionSection
        index={4}
        title={tr("Pricing")}
        hint="What customers pay"
        testid={`${prefix}-section-pricing`}
      >
        <FieldRow
          label={tr("How do you set your pricing?")}
          required
          testid={`${prefix}-field-pricing-basis`}
          error={errors.pricing_basis}
        >
          <RadioCards
            value={option.pricing_basis}
            onChange={(v) =>
              // Switching basis wipes the other branch's data so nothing
              // stale leaks into the payload.
              onChange({
                ...option,
                pricing_basis: v,
                pricing_mode: v === "per_person" ? option.pricing_mode || "" : "",
              })
            }
            options={[
              { value: "per_person", label: "Per person" },
              { value: "per_group", label: "Per group / vehicle" },
            ]}
            testid={`${prefix}-radio-pricing-basis`}
          />
        </FieldRow>

        {option.pricing_basis === "per_person" && (
          <>
            <FieldRow
              label={tr("Capacity: How many participants can you take per time slot?")}
              required
              testid={`${prefix}-field-capacity`}
              error={errors.capacity}
              helper={tr("Minimum can't be lower than 1.")}
            >
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-[#0F1115]">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-[#6B7280]">{tr("Minimum")}</span>
                  <input
                    type="number"
                    min={1}
                    value={option.capacity_min}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const n = raw === "" ? "" : Math.max(1, Number(raw));
                      set("capacity_min", n);
                    }}
                    placeholder="1"
                    data-testid={`${prefix}-input-capacity-min`}
                    style={{ width: "5rem" }}
                    className="humrahe-input h-9 px-2 text-center"
                  />
                </div>
                <span className="text-[#9BA1AB]">{tr("to")}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-[#6B7280]">{tr("Maximum")}</span>
                  <input
                    type="number"
                    min={Number(option.capacity_min) > 0 ? Number(option.capacity_min) : 1}
                    max={CAPACITY_MAX_LIMIT}
                    value={option.capacity_max}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const v = raw === ""
                        ? ""
                        : String(Math.min(CAPACITY_MAX_LIMIT, Math.max(1, Number(raw))));
                      // Resetting the per-person pricing tables on every
                      // max-capacity change avoids stale "Number of people"
                      // bounds — the last tier is locked to capacity_max,
                      // so any old rows would silently bleed into the new
                      // ceiling. Cleaner to start fresh.
                      onChange({
                        ...option,
                        capacity_max: v,
                        flat_tiers: [{ to: "", price: "" }],
                        categories: (option.categories || []).map((c) => ({
                          ...c,
                          tiers: [{ to: "", price: "" }],
                        })),
                      });
                    }}
                    placeholder="20"
                    data-testid={`${prefix}-input-capacity-max`}
                    style={{ width: "6rem" }}
                    className="humrahe-input h-9 px-2 text-center"
                  />
                </div>
                <span className="text-xs text-[#9BA1AB]">{tr("participants")}</span>
              </div>
            </FieldRow>

            <FieldRow
              label={tr("Price model")}
              required
              testid={`${prefix}-field-pricing-mode`}
              error={errors.pricing_mode}
            >
              <RadioCards
                value={option.pricing_mode}
                onChange={(v) => set("pricing_mode", v)}
                options={[
                  {
                    value: "flat",
                    label: "Price is same for everyone",
                    description:
                      "One price applies to every traveller — adults, kids, infants alike.",
                  },
                  {
                    value: "per_category",
                    label: "Price depends on category",
                    description:
                      "Different prices for adults, children, and infants based on their age.",
                  },
                ]}
                testid={`${prefix}-radio-pricing-mode`}
              />
            </FieldRow>

            {option.pricing_mode === "flat" && (
              <FieldRow
                label={tr("Set your pricing")}
                required
                testid={`${prefix}-field-flat-pricing`}
                error={errors.flat_tiers}
                helper={tr("Add tiers if your price changes by group size. The first tier always starts at 1, and the last tier always ends at the maximum capacity above.")}
              >
                <PerPersonTierTable
                  tiers={option.flat_tiers || [{ to: "", price: "" }]}
                  onChange={(tiers) => set("flat_tiers", tiers)}
                  capacityMax={option.capacity_max}
                  testid={`${prefix}-flat-tiers`}
                  currency={currency}
                />
              </FieldRow>
            )}

            {option.pricing_mode === "per_category" && (
              <FieldRow
                label={tr("Set your pricing")}
                required
                testid={`${prefix}-field-categories`}
                error={errors.categories}
                helper={tr("Adult and Child are required. Add Infant if you offer it.")}
              >
                <PerCategoryPricing
                  option={option}
                  setOption={onChange}
                  capacityMax={option.capacity_max}
                  currency={currency}
                />
              </FieldRow>
            )}
          </>
        )}

        {option.pricing_basis === "per_group" && (
          <>
            <FieldRow
              label={tr("Number of people per group / vehicle")}
              required
              testid={`${prefix}-field-group-sizes`}
              error={errors.group_sizes}
              helper={tr("Define each group size your activity supports — e.g. 1 to 4, 5 to 8, 9 to 10.")}
            >
              <GroupSizesEditor option={option} setOption={onChange} />
            </FieldRow>

            <FieldRow
              label={tr("Price per group")}
              required
              testid={`${prefix}-field-group-prices`}
              error={errors.group_prices}
              helper={tr("Include the total amount the customer pays.")}
            >
              <GroupPriceTable option={option} setOption={onChange} currency={currency} />
            </FieldRow>
          </>
        )}
      </OptionSection>

      {/* ---------- 5. Cutoff time ---------- */}
      <OptionSection
        index={5}
        title={tr("Cutoff time")}
        hint="How close to the start time customers can still book"
        testid={`${prefix}-section-cutoff`}
      >
        <FieldRow
          label={tr("Booking cutoff")}
          required
          testid={`${prefix}-field-cutoff`}
          info={tr("How close to the start time customers can still book. Common defaults: 1 hour for last-minute friendly tours, 24 hours for tours that need preparation.")}
        >
          <SelectField
            value={option.cutoff_minutes}
            onChange={(v) => set("cutoff_minutes", Number(v))}
            options={CUTOFF_OPTIONS}
            testid={`${prefix}-select-cutoff`}
          />
        </FieldRow>
      </OptionSection>
    </div>
  );
};

/* ---------- Step entry: list of options + summary cards ---------- */
const Step2Option = ({ data, update, errors = {}, currency = "USD" }) => {
  usePartnerTranslation();
  const options = data.options || [];
  const setOptions = (next) => update("options", next);
  // Track which option is currently expanded for editing. -1 means everything
  // is collapsed (read-only summary). When a brand-new option is added we
  // auto-expand it so the supplier can start filling it in immediately.
  const [editingIdx, setEditingIdx] = useState(options.length > 0 ? 0 : -1);

  useEffect(() => {
    const firstInvalidIdx = errors.options?.findIndex(
      (optionErrors) => optionErrors && Object.keys(optionErrors).length > 0,
    );
    if (firstInvalidIdx >= 0) setEditingIdx(firstInvalidIdx);
  }, [errors.options]);

  const setOptionAt = (idx, next) => {
    const arr = [...options];
    arr[idx] = next;
    setOptions(arr);
  };

  const optionRows = useRef([]);
  const pendingScrollIdx = useRef(null);

  useEffect(() => {
    const idx = pendingScrollIdx.current;
    if (idx === null) return;
    optionRows.current[idx]?.scrollIntoView({ behavior: "smooth", block: "start" });
    pendingScrollIdx.current = null;
  }, [options.length, editingIdx]);

  const addOption = () => {
    const next = [...options, { ...createBlankOption(), currency }];
    pendingScrollIdx.current = next.length - 1;
    setOptions(next);
    setEditingIdx(next.length - 1);
  };

  const cloneOption = (idx) => {
    const src = options[idx];
    const next = [...options];
    next.splice(idx + 1, 0, {
      ...src,
      id: undefined,
      name: `${(src.name || "Option").slice(0, OPTION_NAME_MAX - 7)} (copy)`,
      reference_code: "",
      currency,
    });
    setOptions(next);
    setEditingIdx(idx + 1);
    toast.success(tr("Option duplicated"));
  };

  // -1 means no delete confirmation pending. Lifted out of the row map
  // so it survives re-renders triggered by collapse / expand.
  const [pendingDeleteIdx, setPendingDeleteIdx] = useState(-1);

  const removeOption = (idx) => {
    const next = options.filter((_, i) => i !== idx);
    setOptions(next);
    if (next.length === 0) {
      setEditingIdx(-1);
    } else if (editingIdx === idx) {
      setEditingIdx(Math.min(idx, next.length - 1));
    } else if (editingIdx > idx) {
      setEditingIdx(editingIdx - 1);
    }
  };

  const confirmDelete = () => {
    if (pendingDeleteIdx < 0) return;
    const idx = pendingDeleteIdx;
    setPendingDeleteIdx(-1);
    removeOption(idx);
    toast.success(tr("Option deleted"));
  };

  const pendingOption =
    pendingDeleteIdx >= 0 ? options[pendingDeleteIdx] : null;

  const introCard = (
    <div data-testid="options-intro" className="-mt-2 space-y-2">
      <p className="text-sm leading-relaxed text-[#0F1115]">{tr("Options let you offer variations of the same product to serve different customer needs — varying by duration, group size, language, inclusions, or meeting points.")}</p>
      <p className="text-sm leading-relaxed text-[#6B7280]">{tr("Pricing, availability, and bookings are all managed at the option level. Every product needs at least one option to go live.")}</p>
    </div>
  );

  return (
    <section data-testid="step-option" className="space-y-6">
      <SectionHeader title={tr("Options & pricing")} />

      {introCard}

      {options.length === 0 ? (
        <div
          data-testid="options-empty-state"
          className="rounded-xl border border-dashed border-[#CFD2D8] bg-white px-6 py-10 text-center"
        >
          <Sparkles
            className="mx-auto h-6 w-6 text-[#FF5757]"
            strokeWidth={1.6}
          />
          <p className="mt-3 text-sm font-semibold text-[#0F1115]">{tr("No options yet")}</p>
          <button
            type="button"
            onClick={addOption}
            data-testid="add-option"
            className="humrahe-btn-primary mt-5 inline-flex h-10 items-center gap-1.5 rounded-md px-5 text-sm font-semibold"
          >
            <Plus className="h-4 w-4" />{tr("Create new option")}</button>
        </div>
      ) : (
        <>
          <div className="space-y-3" data-testid="options-list">
            {options.map((opt, idx) => {
              const isExpanded = idx === editingIdx;
              const summary = opt.name?.trim() || `Option ${idx + 1}`;
              return (
                <div
                  key={idx}
                  ref={(node) => { optionRows.current[idx] = node; }}
                  style={{ scrollMarginTop: "7rem" }}
                  data-testid={`option-row-${idx}`}
                  className={cn(
                    "rounded-xl border bg-white shadow-[0_1px_0_rgba(15,17,21,0.04)]",
                    isExpanded ? "border-[#FF8E8E]" : "border-[#D9DDE5]",
                  )}
                >
                  <button
                    type="button"
                    onClick={() => setEditingIdx(isExpanded ? -1 : idx)}
                    data-testid={`option-toggle-${idx}`}
                    aria-expanded={isExpanded}
                    className="flex w-full flex-wrap items-center justify-between gap-2 border-b border-[#F0F2F5] px-5 py-3 text-start"
                  >
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-wider text-[#9BA1AB]">{tr("Option ")}{idx + 1}
                      </p>
                      <p className="truncate text-base font-bold text-[#0F1115]">
                        {summary}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          cloneOption(idx);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            cloneOption(idx);
                          }
                        }}
                        data-testid={`option-clone-${idx}`}
                        aria-label={tr("Duplicate option")}
                        className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-[#6B7280] hover:bg-[#F3EEEE] hover:text-[#0F1115]"
                      >
                        <Copy className="h-4 w-4" />
                      </span>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingDeleteIdx(idx);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            setPendingDeleteIdx(idx);
                          }
                        }}
                        data-testid={`option-remove-${idx}`}
                        aria-label={tr("Remove option")}
                        className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-[#6B7280] hover:bg-[#FFEFEF] hover:text-[#9B0000]"
                      >
                        <Trash2 className="h-4 w-4" />
                      </span>
                      <span
                        aria-hidden
                        className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[#6B7280]"
                      >
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </span>
                    </div>
                  </button>

                  {isExpanded ? (
                    <div className="px-5 py-5">
                      <OptionEditor
                        option={opt}
                        onChange={(next) => setOptionAt(idx, next)}
                        errors={(errors.options && errors.options[idx]) || {}}
                        prefix={`option-${idx}`}
                        currency={currency}
                      />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-3 px-5 py-4 text-sm text-[#6B7280] sm:grid-cols-2">
                      <p>
                        <span className="font-semibold text-[#0F1115]">{tr("Pricing:")}</span>{" "}
                        {opt.pricing_basis === "per_group"
                          ? tr("Per group / vehicle — group sizes: {{count}}", { count: (opt.group_sizes || []).filter((s) => s.to).length })
                          : opt.pricing_basis === "per_person"
                            ? opt.pricing_mode === "flat"
                              ? tr("Per person · same for everyone")
                              : opt.pricing_mode === "per_category"
                                ? tr("Per person · depends on category")
                                : tr("Per person")
                            : "—"}
                      </p>
                      <p>
                        <span className="font-semibold text-[#0F1115]">{tr("Schedule:")}</span>{" "}
                        {tr("Time slots: {{count}}", { count: (opt.schedule_slots || []).length })}
                      </p>
                      <p>
                        <span className="font-semibold text-[#0F1115]">{tr("Languages:")}</span>{" "}
                        {(opt.languages || []).length
                          ? (opt.languages || []).join(", ")
                          : "—"}
                      </p>
                      <p>
                        <span className="font-semibold text-[#0F1115]">{tr("Type:")}</span>{" "}
                        {opt.privacy_type || "—"}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-3 border-t border-dashed border-[#EAECEF] pt-4">
            <button
              type="button"
              onClick={addOption}
              data-testid="add-option"
              className="inline-flex h-10 items-center gap-1.5 rounded-md border border-dashed border-[#0F1115] bg-white px-4 text-sm font-semibold text-[#0F1115] transition-colors hover:bg-[#F3EEEE]"
            >
              <Plus className="h-4 w-4" />{tr("Create new option")}</button>
            <p className="text-xs text-[#6B7280]">{tr("You can also add more options later from the product details page.")}</p>
          </div>
        </>
      )}

      {/* Confirm-delete modal */}
      <Dialog
        open={pendingDeleteIdx >= 0}
        onOpenChange={(v) => !v && setPendingDeleteIdx(-1)}
      >
        <DialogContent
          data-testid="confirm-delete-option-modal"
          className="max-w-[440px] rounded-2xl border-[#EFE4E4] p-0"
        >
          <DialogHeader className="border-b border-[#EFE4E4] px-6 py-5">
            <DialogTitle className="text-lg font-extrabold tracking-tight text-[#0F1115]">{tr("Delete this option?")}</DialogTitle>
            <DialogDescription className="text-sm text-[#6B7280]">
              {pendingOption?.name?.trim()
                ? tr("\"{{value0}}\" will be removed from this product. This action can't be undone.", { value0: pendingOption.name.trim() })
                : tr("This option will be removed from this product. This action can't be undone.")}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-end gap-2 border-t border-[#EFE4E4] px-6 py-4">
            <button
              type="button"
              onClick={() => setPendingDeleteIdx(-1)}
              data-testid="confirm-delete-option-cancel"
              className="inline-flex h-10 items-center rounded-md border border-[#E6E8EC] px-4 text-sm font-semibold text-[#0F1115] transition-colors hover:border-[#0F1115]"
            >{tr("Cancel")}</button>
            <button
              type="button"
              onClick={confirmDelete}
              data-testid="confirm-delete-option-confirm"
              className="inline-flex h-10 items-center rounded-md bg-[#9B0000] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#7A0000]"
            >{tr("Delete option")}</button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default function Step2OptionWithCommission(props) {
  usePartnerTranslation();
  const [rate, setRate] = useState(null);
  useEffect(() => {
    let active = true;
    api.get("/auth/me").then(({ data }) => {
      if (active && typeof data.commission_rate === "number") setRate(data.commission_rate);
    }).catch(() => { if (active) toast.error(tr("Unable to load your commission rate. Reload to see payout estimates.")); });
    return () => { active = false; };
  }, []);
  return <CommissionContext.Provider value={rate}><Step2Option {...props} /></CommissionContext.Provider>;
}
